import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeftRight, TrendingUp, TrendingDown } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import ExportButton from '../../../components/ui/ExportButton';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

function SummaryCard({ label, value, suffix = '', positive }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${positive === true ? 'text-emerald-500' : positive === false ? 'text-red-500' : ''}`}>
          {value}{suffix}
        </p>
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover text-popover-foreground border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value?.toFixed(2)}%</p>
      ))}
    </div>
  );
}

export default function MoneyFlowPage() {
  const navigate = useNavigate();
  const [holder, setHolder] = useState('both');
  const [quarters, setQuarters] = useState(8);
  const [summary, setSummary] = useState(null);
  const [overallData, setOverallData] = useState([]);
  const [sectorData, setSectorData] = useState([]);
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, flowRes, sectorRes, moversRes] = await Promise.all([
        fetch(`${API}/money-flow/summary`),
        fetch(`${API}/money-flow?holder=both&group_by=overall&quarters=${quarters}`),
        fetch(`${API}/money-flow?holder=${holder === 'both' ? 'fii' : holder}&group_by=sector&quarters=4`),
        fetch(`${API}/money-flow/top-movers?holder=${holder === 'both' ? 'fii' : holder}&page_size=15`),
      ]);

      const [sData, fData, secData, mData] = await Promise.all([
        summaryRes.json(), flowRes.json(), sectorRes.json(), moversRes.json(),
      ]);

      setSummary(sData);
      setOverallData(fData.data?.overall || []);
      setMovers(mData.items || []);

      const secEntries = secData.data || {};
      const latestBySector = Object.entries(secEntries).map(([name, points]) => {
        const latest = points[points.length - 1] || {};
        const prev = points[points.length - 2] || {};
        const change = (latest.avg_fii || latest.avg_dii || 0) - (prev.avg_fii || prev.avg_dii || 0);
        return { name, value: change, companies: latest.companies || 0 };
      }).filter(s => s.name).sort((a, b) => b.value - a.value);
      setSectorData(latestBySector);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [holder, quarters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-emerald-500" /> Money Flow Dashboard
        </h1>
        <p className="text-xs text-muted-foreground">Track where institutional money is flowing across sectors</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {['fii', 'dii', 'both'].map(h => (
          <Button key={h} size="sm" variant={holder === h ? 'default' : 'outline'}
            className={`text-xs h-7 ${holder === h ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            onClick={() => setHolder(h)}>
            {h.toUpperCase()}
          </Button>
        ))}
        <div className="flex gap-1 ml-auto">
          {[4, 8, 12].map(q => (
            <Button key={q} size="sm" variant={quarters === q ? 'default' : 'outline'}
              className={`text-xs h-7 ${quarters === q ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setQuarters(q)}>
              {q}Q
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard label="Avg FII Change" value={summary.avg_fii_change?.toFixed(2) || '0'} suffix="%" positive={summary.avg_fii_change > 0} />
              <SummaryCard label="Avg DII Change" value={summary.avg_dii_change?.toFixed(2) || '0'} suffix="%" positive={summary.avg_dii_change > 0} />
              <SummaryCard label="FII Inflows" value={summary.fii_inflow_count || 0} suffix=" stocks" />
              <SummaryCard label="FII Outflows" value={summary.fii_outflow_count || 0} suffix=" stocks" />
            </div>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">FII & DII Holdings Over Time</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={overallData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="quarter" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="avg_fii" name="FII %" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="avg_dii" name="DII %" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {sectorData.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Sector-wise Net Change (Latest Quarter)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(200, sectorData.length * 28)}>
                  <BarChart data={sectorData.slice(0, 20)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 9 }} className="fill-muted-foreground" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Change %">
                      {sectorData.slice(0, 20).map((entry, i) => (
                        <Cell key={i} fill={entry.value >= 0 ? '#22c55e' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Top Movers</h2>
              <ExportButton rows={movers} filename="money-flow-movers" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-emerald-800 text-white">
                    <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                    <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Sector</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Prev %</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Latest %</th>
                    <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {movers.map((r, i) => (
                    <tr key={i} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                      onClick={() => r.symbol && navigate(`/public/india-company/${r.symbol}`)}>
                      <td className="py-2 px-3 font-medium">{r.company_name}</td>
                      <td className="py-2 px-3 text-muted-foreground">{r.sector || '—'}</td>
                      <td className="py-2 px-3 text-right">{r.pct_prev?.toFixed(2) ?? '—'}%</td>
                      <td className="py-2 px-3 text-right font-medium">{r.pct_latest?.toFixed(2) ?? '—'}%</td>
                      <td className="py-2 px-3 text-right">
                        <span className={`font-semibold ${r.change_pct > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {r.change_pct > 0 ? '+' : ''}{r.change_pct?.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
